import { format } from 'date-fns';
import { resolveWhatsAppMediaUrl } from './mediaUrl';
import { normalizeDirection, normalizeStatus } from './messageDirection';

export { normalizeDirection, normalizeStatus };

export function formatMediaUrl(rawUrl) {
  if (!rawUrl) return null;
  const resolved = resolveWhatsAppMediaUrl(rawUrl);
  if (!resolved) return null;
  if (
    resolved.includes('ngrok') &&
    !resolved.includes('ngrok-skip-browser-warning')
  ) {
    const delimiter = resolved.includes('?') ? '&' : '?';
    return `${resolved}${delimiter}ngrok-skip-browser-warning=true`;
  }
  return resolved;
}

/**
 * Groups raw WhatsApp/Instagram messages (returned in descending order by the API)
 * into date-labeled groups in ascending order (oldest at top, newest at bottom).
 *
 * @param {Array} rawMessages  - Flat array of message objects (descending from API)
 * @param {Object} reactions   - Map of messageId -> emoji[] from local optimistic state
 * @returns {Array} - Array of { date: string, messages: Array } in ascending date order
 */
export function groupMessagesByDate(rawMessages, reactions = {}) {
  if (!rawMessages || rawMessages.length === 0) return [];

  const grouped = {};

  rawMessages.forEach((msg) => {
    let dateKey = 'Unknown Date';
    if (msg.timestamp) {
      try {
        const d = new Date(msg.timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (d.toDateString() === today.toDateString()) {
          dateKey = 'Today';
        } else if (d.toDateString() === yesterday.toDateString()) {
          dateKey = 'Yesterday';
        } else {
          dateKey = format(d, 'MMMM d, yyyy');
        }
      } catch {
        dateKey = msg.timestamp.split('T')[0];
      }
    }
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(msg);
  });

  const uniqueDatesDesc = [
    ...new Set(
      rawMessages.map((msg) => {
        if (!msg.timestamp) return 'Unknown Date';
        try {
          const d = new Date(msg.timestamp);
          const today = new Date();
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          if (d.toDateString() === today.toDateString()) return 'Today';
          if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
          return format(d, 'MMMM d, yyyy');
        } catch {
          return msg.timestamp.split('T')[0];
        }
      }),
    ),
  ];

  return uniqueDatesDesc.reverse().map((dateKey) => {
    const msgs = grouped[dateKey]
      .map((msg) => {
        const firstAttachment = msg.attachments?.[0];
        const templateHeader = msg.template?.header;
        const rawUrl =
          firstAttachment?.file_url ||
          firstAttachment?.file ||
          templateHeader?.url ||
          templateHeader?.file ||
          msg.template?.media_url ||
          msg.template?.image_url ||
          msg.file_url ||
          msg.file ||
          msg.mediaUrl;
        const mediaUrl = formatMediaUrl(rawUrl);

        const messageType = msg.message_type;
        const headerFormat = (templateHeader?.format || '').toUpperCase();
        const isImage =
          headerFormat === 'IMAGE' ||
          (rawUrl &&
            typeof rawUrl === 'string' &&
            /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(rawUrl));
        const isVideo =
          headerFormat === 'VIDEO' ||
          (rawUrl &&
            typeof rawUrl === 'string' &&
            /\.(mp4|mov|webm)(\?.*)?$/i.test(rawUrl));
        const isDoc =
          headerFormat === 'DOCUMENT' ||
          (rawUrl &&
            typeof rawUrl === 'string' &&
            /\.(pdf|doc|docx|xls|xlsx|csv)(\?.*)?$/i.test(rawUrl));

        const mimeType =
          firstAttachment?.mime_type ||
          templateHeader?.mime_type ||
          (firstAttachment?.file_type
            ? firstAttachment.file_type.includes('/')
              ? firstAttachment.file_type
              : `${firstAttachment.file_type}/*`
            : null) ||
          (isImage
            ? 'image/png'
            : isVideo
              ? 'video/mp4'
              : isDoc
                ? 'application/pdf'
                : messageType === 'audio'
                  ? 'audio/*'
                  : messageType === 'image'
                    ? 'image/*'
                    : messageType === 'video'
                      ? 'video/*'
                      : null);

        const buttons =
          msg.template?.buttons ||
          msg.template?.components?.find((c) => c.type === 'BUTTONS')
            ?.buttons ||
          msg.buttons ||
          [];

        return {
          id: msg.id?.toString(),
          content: msg.body,
          direction: normalizeDirection(msg),
          time: msg.timestamp
            ? (() => {
                try {
                  return format(new Date(msg.timestamp), 'hh:mm a');
                } catch {
                  return msg.timestamp;
                }
              })()
            : '',
          status: normalizeStatus(msg, normalizeDirection(msg)),
          messageType: messageType,
          mediaUrl: mediaUrl,
          mimeType: mimeType,
          fileName:
            firstAttachment?.file_name ||
            firstAttachment?.filename ||
            firstAttachment?.name,
          fileSize:
            firstAttachment?.file_size ||
            firstAttachment?.filesize ||
            firstAttachment?.size,
          timestamp: msg.timestamp,
          isDeleted: msg.is_deleted,
          reaction:
            reactions[msg.id?.toString()] !== undefined
              ? reactions[msg.id?.toString()]
              : msg.reaction || [],
          buttons: buttons,
          template: msg.template || null,
          attachments: msg.attachments || [],
        };
      })
      .reverse();

    return { date: dateKey, messages: msgs };
  });
}
